/**
 * Card de configuración del gabinete: toggles admin-only para las opciones
 * de IA con impacto de coste.
 *
 * Actualmente:
 *  - `ai_consolidation_enabled`: activa la consolidación multi-vista por
 *    estancia (llama a Claude una vez por estancia con todas sus fotos).
 *    OFF por defecto → coste mínimo, se usa solo el promedio simple.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Loader2, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export function OrgSettingsCard({ canEdit }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/org/settings");
        setSettings(r.data?.settings || {});
      } catch (_) {
        setSettings({});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (key, next) => {
    setSaving(true);
    try {
      const r = await api.put("/org/settings", { [key]: next });
      setSettings(r.data?.settings || settings);
      toast.success("Configuración actualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-4">
        <div className="flex items-center gap-2 text-sm text-[#7A786F]">
          <Loader2 className="h-3 w-3 animate-spin" /> Cargando configuración…
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-5" data-testid="org-settings-card">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-[#D85A30]" />
        <h3 className="font-heading text-base font-semibold text-[#2C2C2A]">Configuración del gabinete — IA</h3>
      </div>

      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 pb-3 border-b border-[#E5E2D8]">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#2C2C2A]">
              Consolidación IA multi-vista por estancia
            </div>
            <p className="text-xs text-[#7A786F] mt-0.5">
              Cuando está <b>activada</b>, tras cada valoración la IA analiza todas las fotos de cada estancia a la vez y devuelve una dimensión refinada (más precisa pero con <b>coste extra</b>: una llamada de visión por estancia, ~0,05-0,10 € cada una).
              <br />
              Cuando está <b>desactivada</b> (por defecto), solo se usa el <b>promedio simple</b> de las mediciones individuales — <b>sin coste adicional</b>.
            </p>
            {!canEdit && (
              <p className="text-[11px] text-[#B98A18] mt-1.5 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Solo el admin del gabinete puede modificar esta opción.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-1">
            {saving && <Loader2 className="h-3 w-3 animate-spin text-[#7A786F]" />}
            <Switch
              checked={!!settings?.ai_consolidation_enabled}
              onCheckedChange={(v) => toggle("ai_consolidation_enabled", v)}
              disabled={!canEdit || saving}
              data-testid="toggle-ai-consolidation"
            />
          </div>
        </div>

        <p className="text-[10px] text-[#7A786F] italic">
          Las mediciones por foto (botón &quot;Medir&quot;) y las descripciones automáticas siguen siendo gratuitas dentro de tu Universal Key. Estas opciones controlan solo las llamadas <b>extra</b> con coste significativo.
        </p>
      </div>
    </div>
  );
}
